<template>
  <div :id="id" class="web-instagram-feed" :class="css">
    <b-overlay :show="loading" rounded="lg" opacity="0.6">
      <div class="web-instagram-feed__header">
        <div>
          <div class="web-instagram-feed__eyebrow">Instagram</div>
          <h3 class="web-instagram-feed__title">
            Ultimas publicaciones
          </h3>
          <p class="web-instagram-feed__subtitle" v-if="username">
            @{{ username }}
          </p>
        </div>
        <a
          v-if="profileUrl"
          :href="profileUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="btn btn-outline-primary web-instagram-feed__button"
        >
          Ver perfil
        </a>
      </div>

      <div v-if="posts.length > 0" class="row">
        <div
          v-for="post in posts"
          :key="post.id || post.permalink"
          class="col-6 col-md-4 mb-4"
        >
          <a
            :href="post.permalink || profileUrl || '#'"
            target="_blank"
            rel="noopener noreferrer"
            class="web-instagram-feed__card"
          >
            <div class="web-instagram-feed__media">
              <img
                v-if="post.mediaUrl"
                :src="post.mediaUrl"
                :alt="post.caption || 'Publicación de Instagram'"
                class="web-instagram-feed__image"
              />
              <div
                v-else
                class="web-instagram-feed__placeholder"
              >
                <font-awesome-icon icon="camera" />
              </div>
              <span
                v-if="post.mediaType === 'VIDEO'"
                class="web-instagram-feed__type"
              >
                <font-awesome-icon icon="play" />
              </span>
            </div>
            <div class="web-instagram-feed__body">
              <div class="web-instagram-feed__date" v-if="post.timestamp">
                {{ formatDate(post.timestamp) }}
              </div>
              <p class="web-instagram-feed__caption">
                {{ trimCaption(post.caption) }}
              </p>
            </div>
          </a>
        </div>
      </div>

      <div v-else class="web-instagram-feed__empty">
        <font-awesome-icon icon="camera" class="web-instagram-feed__empty-icon" />
        <div class="web-instagram-feed__empty-title">
          Aun no hay publicaciones disponibles
        </div>
        <div class="web-instagram-feed__empty-text">
          {{ emptyMessage }}
        </div>
        <a
          v-if="profileUrl"
          :href="profileUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="btn btn-primary mt-3"
        >
          Ir a Instagram
        </a>
      </div>
    </b-overlay>
  </div>
</template>

<script>
export default {
  name: "instagramFeed",
  props: {
    id: {
      type: String,
      default: "",
    },
    css: {
      type: String,
      default: "",
    },
    params: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      loading: true,
      posts: [],
      username: "",
      profileUrl: "",
      connected: false,
      limit: 6,
    };
  },
  computed: {
    emptyMessage() {
      if (this.profileUrl) {
        return "Conecta Instagram Business o revisa el perfil para ver las publicaciones mas recientes.";
      }

      return "Este negocio aun no tiene una cuenta de Instagram conectada.";
    },
  },
  mounted() {
    this.applyParams();
    this.load();
  },
  methods: {
    applyParams() {
      if (!this.params) return;

      this.params.split(",").forEach((param) => {
        const [rawKey, rawValue] = param.split("=");
        const key = `${rawKey || ""}`.trim();
        const value = `${rawValue || ""}`.trim();

        if (key === "limit" && Number(value) > 0) {
          this.limit = Number(value);
        }
      });
    },
    load() {
      const { webName, instagram } = this.$store.state.info || {};
      if (instagram) {
        this.profileUrl = instagram;
      }

      if (!webName) {
        this.loading = false;
        return;
      }

      this.$http
        .get(`${webName}/instagram-feed?limit=${this.limit}`)
        .then((res) => {
          const data = (res && res.data && res.data.data) || {};
          this.posts = Array.isArray(data.items) ? data.items : [];
          this.username = data.username || "";
          this.connected = Boolean(data.connected);
          this.profileUrl = data.profileUrl || this.profileUrl;
        })
        .catch(() => {
          this.posts = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },
    trimCaption(value = "") {
      const caption = `${value || ""}`.trim();
      if (!caption) return "Ver publicación";
      if (caption.length <= 110) return caption;
      return `${caption.slice(0, 107)}...`;
    },
    formatDate(value) {
      if (!value) return "";

      try {
        return new Intl.DateTimeFormat("es-CL", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }).format(new Date(value));
      } catch (error) {
        return "";
      }
    },
  },
};
</script>

<style scoped>
.web-instagram-feed {
  width: 100%;
}

.web-instagram-feed__header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.web-instagram-feed__eyebrow {
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #7c8aa5;
  margin-bottom: 0.35rem;
}

.web-instagram-feed__title {
  margin: 0;
  font-size: 1.8rem;
  font-weight: 800;
  color: #24364f;
}

.web-instagram-feed__subtitle {
  margin: 0.35rem 0 0;
  color: #60708c;
}

.web-instagram-feed__button {
  white-space: nowrap;
}

.web-instagram-feed__card {
  display: block;
  height: 100%;
  background: #fff;
  border: 1px solid #dce7f2;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 16px 32px rgba(27, 55, 89, 0.08);
  transition: transform 0.18s ease, box-shadow 0.18s ease;
}

.web-instagram-feed__card:hover {
  transform: translateY(-4px);
  box-shadow: 0 22px 38px rgba(27, 55, 89, 0.13);
  text-decoration: none;
}

.web-instagram-feed__media {
  position: relative;
  aspect-ratio: 1 / 1;
  background: linear-gradient(135deg, #f4f7fb 0%, #e7eef8 100%);
}

.web-instagram-feed__image,
.web-instagram-feed__placeholder {
  width: 100%;
  height: 100%;
}

.web-instagram-feed__image {
  object-fit: cover;
  display: block;
}

.web-instagram-feed__placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #8aa0bd;
  font-size: 2rem;
}

.web-instagram-feed__type {
  position: absolute;
  top: 0.8rem;
  right: 0.8rem;
  width: 34px;
  height: 34px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: rgba(27, 39, 60, 0.75);
}

.web-instagram-feed__body {
  padding: 1rem 1rem 1.1rem;
}

.web-instagram-feed__date {
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: #8191ab;
  margin-bottom: 0.45rem;
}

.web-instagram-feed__caption {
  margin: 0;
  color: #30435f;
  line-height: 1.45;
}

.web-instagram-feed__empty {
  border: 1px dashed #cddbeb;
  border-radius: 22px;
  background: #f8fbff;
  padding: 2.5rem 1.5rem;
  text-align: center;
  color: #5d7090;
}

.web-instagram-feed__empty-icon {
  font-size: 2rem;
  color: #7c90ad;
  margin-bottom: 0.9rem;
}

.web-instagram-feed__empty-title {
  font-size: 1.05rem;
  font-weight: 800;
  color: #2d405d;
  margin-bottom: 0.35rem;
}

@media (max-width: 767px) {
  .web-instagram-feed__header {
    flex-direction: column;
    align-items: flex-start;
  }

  .web-instagram-feed__title {
    font-size: 1.45rem;
  }
}
</style>
